import { AxiosError, AxiosResponse } from "axios"
import Swal, { SweetAlertOptions } from "sweetalert2"

type TPayload = {
  text?: string
  preConfirm?: SweetAlertOptions["preConfirm"]
  promise?: () => Promise<AxiosResponse<any>>
}

const textConfirmDestroyDefault = "You won't be able to revert this!"

export class SwalConfig {
  static confirmDestroy({
    text = textConfirmDestroyDefault,
    preConfirm,
    promise,
  }: TPayload): SweetAlertOptions {
    if (!preConfirm && !promise) {
      console.warn("It must have at least one of the 2 parameters preConfirm, promise")
      return {}
    }

    let preConfirmHandler
    if (promise) {
      preConfirmHandler = async () => {
        try {
          Swal.showLoading()
          const { status } = await promise()
          if (status === 204) {
            return new Promise((resolve) => resolve(true))
          }
        } catch (error) {
          if (error instanceof AxiosError) {
            if (error.response?.data) {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response.data?.msg || error.response.data?.errors?.message || "Error",
              })
              throw error.response?.data
            }
          }
        }
      }
    } else {
      preConfirmHandler = preConfirm
    }

    return {
      title: "Are you sure?",
      text,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: preConfirmHandler,
    }
  }
  static validationError(errors: Record<string, string>, message?: string, children?: string) {
    Swal.fire({
      showConfirmButton: true,
      focusConfirm: true,
      // icon: "error",
      html: `
        <div class="p-4 sm:p-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg text-left">
          <div class="flex items-center mb-4">
            <svg class="w-8 h-8 text-red-600 dark:text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 class="text-xl font-bold text-red-700 dark:text-red-500">${message || "A critical error occurred!"}</h3>
          </div>
          <p class="text-slate-700 dark:text-slate-400 mb-4 text-base">
            We regret to inform you that the operation could not be completed. Please review the following points:
          </p>
          <ul class="list-disc pl-5 space-y-2 text-sm text-slate-800 dark:text-slate-400">
            ${Object.entries(errors)
              .map(
                ([key, value]) =>
                  `<li class="font-medium"><span class="font-bold capitalize">${key.replaceAll("_", " ")}:</span> ${value}</li>`,
              )
              .join("")}
          </ul>
          ${children || ""}
        </div>
      `,
      customClass: {
        container: "p-0",
        confirmButton:
          "bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out shadow-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50",
      },
      confirmButtonText: "Understood",
      allowOutsideClick: false,
      allowEscapeKey: false,
    })
  }
}
